import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, TextInput, Select } from "@/components";
import type {
  ContentStrategyStatus,
  ContentStrategyMode,
  ContentStrategyDurationPeriod,
} from "../interfaces";

const DURATION_PERIOD_OPTIONS: { value: ContentStrategyDurationPeriod; label: string }[] =
  [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
    { value: "indefinite", label: "Indefinite" },
  ];

const STATUS_OPTIONS: { value: ContentStrategyStatus; label: string }[] = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
];

const MODE_OPTIONS: { value: ContentStrategyMode; label: string }[] = [
  { value: "AUTO", label: "Auto" },
  { value: "REQUIRE_REVIEW", label: "Require Review" },
];

const settingsSchema = yup.object({
  status: yup.string().oneOf(["ACTIVE", "DRAFT"]).required(),
  mode: yup.string().oneOf(["AUTO", "REQUIRE_REVIEW"]).required(),
  period: yup.string().oneOf(["daily", "weekly", "monthly", "quarterly", "yearly", "indefinite"]).required(),
  periodCount: yup.number().min(1).required(),
});

type SettingsFormData = {
  status: ContentStrategyStatus;
  mode: ContentStrategyMode;
  period: ContentStrategyDurationPeriod;
  periodCount: number;
};

interface StrategySettingsStepProps {
  initialStatus?: ContentStrategyStatus;
  initialMode?: ContentStrategyMode;
  initialPeriod?: ContentStrategyDurationPeriod;
  initialPeriodCount?: number;
  onNext: (settings: {
    status: ContentStrategyStatus;
    mode: ContentStrategyMode;
    duration: { period: ContentStrategyDurationPeriod; periodCount: number };
  }) => void;
  onBack: () => void;
}

export const StrategySettingsStep: React.FC<StrategySettingsStepProps> = ({
  initialStatus = "DRAFT",
  initialMode = "REQUIRE_REVIEW",
  initialPeriod = "weekly",
  initialPeriodCount = 1,
  onNext,
  onBack,
}) => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<SettingsFormData>({
    resolver: yupResolver(settingsSchema) as any,
    defaultValues: {
      status: initialStatus,
      mode: initialMode,
      period: initialPeriod,
      periodCount: initialPeriodCount,
    },
  });

  const onFormSubmit = (data: SettingsFormData) => {
    onNext({
      status: data.status,
      mode: data.mode,
      duration: {
        period: data.period,
        periodCount: data.periodCount,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={watch("status")}
          onChange={(val) => setValue("status", val as ContentStrategyStatus)}
        />
        <Select
          label="Mode"
          options={MODE_OPTIONS}
          value={watch("mode")}
          onChange={(val) => setValue("mode", val as ContentStrategyMode)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Duration Period"
          options={DURATION_PERIOD_OPTIONS}
          value={watch("period")}
          onChange={(val) => setValue("period", val as ContentStrategyDurationPeriod)}
        />
        <TextInput
          label="Duration Count"
          type="number"
          min={1}
          error={!!errors.periodCount}
          helperText={errors.periodCount?.message}
          {...register("periodCount", { valueAsNumber: true })}
        />
      </div>

      <div className="flex gap-4 justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit">Next</Button>
      </div>
    </form>
  );
};
